import React from "react";
import { convertTimestampToDate } from "../../../../utils";
import ProjectDetailsBox from "./ProjectDetailsBox";
import ProjectCheckpoints from "./ProjectCheckpoints";

export default function CompletedProjectDetails({ project }) {
  const getMilestoneStatusLabel = status => {
    switch (status) {
      case "waiting_approval":
        return { text: "Waiting For Approval", className: "statusWaiting" };
      case "completed":
        return { text: "Completed", className: "statusCompleted" };
      case "not_completed":
      default:
        return { text: "Not Completed", className: "statusPending" };
    }
  };

  return (
    <div className="projectDetailsContainer">
      {/* Project ID and title */}
      <div className="headerWithBack">
        <div className="headerProjectTitle">{project.title}</div>
        <div className="projectId">#{project.projectId}</div>
      </div>

      {/* Project details box */}
      <ProjectDetailsBox project={project} />

      {/* Completion details */}
      <div className="completionDetails">
        <div className="completionHeader">Project Completion Details</div>
        <div className="completionContent">
          <div className="completionRow">
            <div className="completionLabel">Completion Date</div>
            <div className="completionValue">
              {convertTimestampToDate(project.completedOn)}
            </div>
          </div>
          <div className="completionRow">
            <div className="completionLabel">Final Amount</div>
            <div className="completionValue">{project.priceFinalized} SAR</div>
          </div>
          {project.completionNotes && (
            <div className="completionRow">
              <div className="completionLabel">Completion Notes</div>
              <div className="completionValue">{project.completionNotes}</div>
            </div>
          )}
        </div>
      </div>

      {/* Project completion status */}
      <div className="projectDetailsBox" style={{ marginTop: "2vh" }}>
        <div
          className="projectDetailRow"
          style={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <div className="completedStatusBadge">Completed</div>
          <div
            className="projectDetailValue"
            style={{ color: "#10b981", fontWeight: 600 }}
          >
            Finalized
          </div>
        </div>

        <div className="projectDetailRow" style={{ marginTop: "1vh" }}>
          <div className="projectDetailColumn" style={{ width: "100%" }}>
            <div className="milestoneCompletion">
              <div className="milestoneProgressLabel">
                Project Completion : 100%
              </div>
              <div className="progressBar">
                <div className="progressFill" style={{ width: "100%" }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Completed Checkpoints section */}
      <ProjectCheckpoints
        checkpoints={project.checkpoints}
        isCompleted={true}
        projectPriceFinalized={project.priceFinalized}
      />

      {/* Bills section */}
      <div className="projectDetailsBox">
        <div className="projectDetailsHeader">Expense Records</div>

        {project.bills && project.bills.length > 0 ? (
          <>
            {project.bills.map(bill => (
              <div className="bidCard" key={bill.id}>
                <div className="bidHeader">
                  <div className="bidMerchantName">
                    {bill.merchFinalizedData.displayName}
                  </div>
                  <div className="bidAmount">{bill.amount} SAR</div>
                </div>
                <div className="bidContent">{bill.description}</div>
                <div className="bidDate">
                  {convertTimestampToDate(bill.timestamp)}
                </div>

                {/* Bill Images */}
                {bill.images && bill.images.length > 0 && (
                  <div className="billImagesContainer">
                    <div className="projectImagesGrid">
                      {bill.images.map(image => (
                        <div key={image.id} className="projectImageContainer">
                          <img
                            src={image.url}
                            alt="Bill"
                            className="projectImage"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div className="billSummary">
              <div className="billSummaryLabel">Total Expenses</div>
              <div className="billSummaryValue">
                {project.billsTotal} SAR (Finalized)
              </div>
            </div>
          </>
        ) : (
          <div className="emptyState">
            <div className="emptyStateText">
              No expense records available for this project
            </div>
          </div>
        )}
      </div>

      {/* Feedback section */}
      {project.feedback && (
        <div className="projectDetailsBox">
          <div className="projectDetailsHeader">Customer Feedback</div>
          <div className="feedbackCard">
            <div className="feedbackRating">
              Rating:{" "}
              <span className="ratingValue">{project.feedback.rating}/5</span>
            </div>
            <div className="feedbackComment">"{project.feedback.comment}"</div>
            <div className="bidDate">
              {convertTimestampToDate(project.feedback.timestamp)}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .projectDetailsContainer {
          width: 100%;
          box-sizing: border-box;
          padding: 24px;
          background-color: #fff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          overflow-x: hidden;
        }

        .headerWithBack {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 24px;
        }

        .headerProjectTitle {
          font-size: 24px;
          font-weight: 600;
          color: #333;
        }

        .projectId {
          font-size: 16px;
          color: #8c8c8c;
          font-weight: 500;
        }

        .completionDetails {
          width: 100%;
          box-sizing: border-box;
          margin-top: 24px;
          padding: 24px;
          background-color: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .completionHeader {
          font-size: 18px;
          font-weight: 600;
          color: #333;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #e9ecef;
        }

        .completionContent {
          align-items: center;
          justify-content: center;
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 24px;
        }

        .completionRow {
          flex: 1;
          min-width: 200px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .completionLabel {
          font-size: 14px;
          color: #666;
          font-weight: 500;
        }

        .completionValue {
          font-size: 16px;
          color: #333;
        }

        .projectDetailsBox {
          width: 100%;
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
}
